import { useEffect, useRef, useState } from "react";

/** Nothing is slow until it has been this long. Below it, wait. */
export const LOADER_AFTER_MS = 300;
/** Once shown, a loader stays at least this long. */
export const LOADER_AT_LEAST_MS = 400;

/**
 * Whether to show a loader for something that is `busy`.
 *
 * Two rules, and the second matters as much as the first.
 *
 * A request that answers in 80ms should show nothing at all — a skeleton that
 * appears and vanishes inside a tenth of a second reads as a glitch, not as
 * progress, and it makes a fast screen feel unreliable. So the loader waits
 * {@link LOADER_AFTER_MS} before appearing.
 *
 * And having appeared, it stays for {@link LOADER_AT_LEAST_MS}. Otherwise a
 * response landing at 310ms produces a ten-millisecond flash, which is the
 * same glitch moved later. The floor costs a little time on the unluckiest
 * responses and buys every screen the property that a loader, once seen, is
 * seen properly.
 */
export function useDelayed(
  busy: boolean,
  { after = LOADER_AFTER_MS, atLeast = LOADER_AT_LEAST_MS } = {},
): boolean {
  const [showing, setShowing] = useState(false);
  const shownAt = useRef(0);

  useEffect(() => {
    if (busy) {
      const appear = setTimeout(() => {
        shownAt.current = Date.now();
        setShowing(true);
      }, after);
      return () => clearTimeout(appear);
    }
    if (!showing) return;
    const left = shownAt.current + atLeast - Date.now();
    if (left <= 0) {
      setShowing(false);
      return;
    }
    const linger = setTimeout(() => setShowing(false), left);
    return () => clearTimeout(linger);
  }, [busy, showing, after, atLeast]);

  return showing;
}
